import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const GRID = 20;
const MIN_W = 40;
const MIN_H = 40;

const PosFloorEditor = () => {
  const [boot, setBoot] = useState({
    loading: true,
    error: '',
    shop_id: 0,
    canvas_w: 1000,
    canvas_h: 600,
    bg: '',
    tables: [],
  });

  const [canvasW, setCanvasW] = useState(1000);
  const [canvasH, setCanvasH] = useState(600);
  const [bgPath, setBgPath] = useState('');
  const [newName, setNewName] = useState('');
  const [newShape, setNewShape] = useState('rect');

  const [selectedId, setSelectedId] = useState(null);
  const [selectedName, setSelectedName] = useState('');
  const [selectedShape, setSelectedShape] = useState('rect');
  const [selectedActive, setSelectedActive] = useState(1);

  const [toasts, setToasts] = useState([]);

  const floorRef = useRef(null);

  const dragRef = useRef({
    active: false,
    mode: null,
    id: null,
    startX: 0,
    startY: 0,
    origLeft: 0,
    origTop: 0,
    origW: 0,
    origH: 0,
  });

  const drawRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  });

  const toast = useCallback((msg, ok = true) => {
    const id = `${Date.now()}_${Math.random()}`;
    setToasts((prev) => [...prev, { id, msg, ok }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 1600);
  }, []);

  const post = useCallback(async (action, data) => {
    const fd = new FormData();
    fd.append('action', action);
    Object.entries(data || {}).forEach(([k, v]) => fd.append(k, v));
    const res = await fetch('/api/pos/floor-editor/action', {
      method: 'POST',
      body: fd,
      credentials: 'same-origin',
    });
    return await res.json();
  }, []);

  const snap = useCallback((n) => {
    return Math.round(n / GRID) * GRID;
  }, []);

  const clamp = useCallback((n, min, max) => {
    return Math.max(min, Math.min(max, n));
  }, []);

  const getFloorRect = useCallback(() => {
    return floorRef.current.getBoundingClientRect();
  }, []);

  const getFloorXYFromClient = useCallback((clientX, clientY) => {
    const rect = getFloorRect();
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  }, [getFloorRect]);

  const getFloorSize = useCallback(() => {
    return {
      w: floorRef.current?.clientWidth || 0,
      h: floorRef.current?.clientHeight || 0,
    };
  }, []);

  const loadBootstrap = useCallback(async () => {
    try {
      const res = await fetch('/api/pos/floor-editor/bootstrap', {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      const data = await res.json();

      if (!data.success) {
        setBoot((prev) => ({
          ...prev,
          loading: false,
          error: data.message || '초기화 실패',
        }));
        return;
      }

      setBoot({
        loading: false,
        error: '',
        shop_id: Number(data.shop_id || 0),
        canvas_w: Number(data.canvas_w || 1000),
        canvas_h: Number(data.canvas_h || 600),
        bg: data.bg || '',
        tables: Array.isArray(data.tables) ? data.tables : [],
      });

      setCanvasW(Number(data.canvas_w || 1000));
      setCanvasH(Number(data.canvas_h || 600));
      setBgPath(data.bg || '');
    } catch (e) {
      setBoot((prev) => ({
        ...prev,
        loading: false,
        error: e.message || '초기화 실패',
      }));
    }
  }, []);

  useEffect(() => {
    loadBootstrap();
  }, [loadBootstrap]);

  const selectedTable = useMemo(() => {
    return boot.tables.find((t) => Number(t.id) === Number(selectedId)) || null;
  }, [boot.tables, selectedId]);

  useEffect(() => {
    if (!selectedTable) {
      setSelectedName('');
      setSelectedShape('rect');
      setSelectedActive(1);
      return;
    }
    setSelectedName(selectedTable.name || '');
    setSelectedShape(selectedTable.shape || 'rect');
    setSelectedActive(Number(selectedTable.active || 0) === 1 ? 1 : 0);
  }, [selectedTable]);

  const floorStyle = useMemo(() => {
    const bgImage = boot.bg
      ? `
        linear-gradient(var(--grid-line) 1px, transparent 1px),
        linear-gradient(90deg, var(--grid-line) 1px, transparent 1px),
        url('${boot.bg}')
      `
      : `
        linear-gradient(var(--grid-line) 1px, transparent 1px),
        linear-gradient(90deg, var(--grid-line) 1px, transparent 1px)
      `;

    const bgSize = boot.bg
      ? `
        var(--grid-size) var(--grid-size),
        var(--grid-size) var(--grid-size),
        cover
      `
      : `
        var(--grid-size) var(--grid-size),
        var(--grid-size) var(--grid-size)
      `;

    const bgPosition = boot.bg ? '0 0, 0 0, center center' : '0 0, 0 0';
    const bgRepeat = boot.bg ? 'repeat, repeat, no-repeat' : 'repeat, repeat';

    return {
      width: `${boot.canvas_w}px`,
      height: `${boot.canvas_h}px`,
      backgroundColor: 'var(--floor-bg)',
      backgroundImage: bgImage,
      backgroundSize: bgSize,
      backgroundPosition: bgPosition,
      backgroundRepeat: bgRepeat,
    };
  }, [boot.bg, boot.canvas_w, boot.canvas_h]);

  const updateTableLocal = useCallback((id, patch) => {
    setBoot((prev) => ({
      ...prev,
      tables: prev.tables.map((t) =>
        Number(t.id) === Number(id) ? { ...t, ...patch } : t
      ),
    }));
  }, []);

  const setSelected = useCallback((id) => {
    setSelectedId(id ? Number(id) : null);
  }, []);

  const handleApplyFloor = useCallback(() => {
    setBoot((prev) => ({
      ...prev,
      canvas_w: Number(canvasW || 1000),
      canvas_h: Number(canvasH || 600),
      bg: String(bgPath || '').trim(),
    }));
    toast('화면에 적용됨');
  }, [bgPath, canvasH, canvasW, toast]);

  const handleSaveFloor = useCallback(async () => {
    const r = await post('save_floorplan', {
      canvas_width: canvasW,
      canvas_height: canvasH,
      background_image: bgPath,
    });

    if (!r.success) {
      toast(r.message || '저장 실패', false);
      return;
    }
    toast('도면 저장됨');
  }, [bgPath, canvasH, canvasW, post, toast]);

  const handleAddTable = useCallback(async () => {
    const existing = boot.tables.length;
    const colCount = Math.max(1, Math.floor((boot.canvas_w - GRID) / (120 + GRID)));
    const col = existing % colCount;
    const row = Math.floor(existing / colCount);

    const x = snap(GRID + col * (120 + GRID));
    const y = snap(GRID + row * (80 + GRID));

    const r = await post('add_table', {
      name: newName.trim(),
      shape: newShape,
      x,
      y,
      w: 120,
      h: 80,
    });

    if (!r.success) {
      toast(r.message || '추가 실패', false);
      return;
    }

    const t = r.table;
    setBoot((prev) => ({
      ...prev,
      tables: [...prev.tables, t],
    }));
    setSelected(t.id);
    toast('테이블 추가됨');
  }, [boot.canvas_w, boot.tables.length, newName, newShape, post, setSelected, snap, toast]);

  const handleMetaSave = useCallback(async () => {
    if (!selectedId) return;

    const r = await post('update_table_meta', {
      id: selectedId,
      name: selectedName.trim(),
      shape: selectedShape,
      active: selectedActive,
    });

    if (!r.success) {
      toast(r.message || '실패', false);
      return;
    }

    updateTableLocal(selectedId, {
      name: selectedName.trim() || '테이블',
      shape: selectedShape,
      active: Number(selectedActive) === 1 ? 1 : 0,
    });

    toast('저장됨');
  }, [post, selectedActive, selectedId, selectedName, selectedShape, toast, updateTableLocal]);

  const handleDelete = useCallback(async () => {
    if (!selectedId) return;
    if (!window.confirm('정말 삭제?')) return;

    const r = await post('delete_table', { id: selectedId });
    if (!r.success) {
      toast(r.message || '삭제 실패', false);
      return;
    }

    setBoot((prev) => ({
      ...prev,
      tables: prev.tables.filter((t) => Number(t.id) !== Number(selectedId)),
    }));
    setSelected(null);
    toast('삭제됨');
  }, [post, selectedId, setSelected, toast]);

  const saveTablePosition = useCallback(async (t) => {
    const r = await post('save_table', {
      id: t.id,
      x: t.x,
      y: t.y,
      w: t.w,
      h: t.h,
    });
    if (!r.success) {
      toast(r.message || '저장 실패', false);
      return;
    }
    toast('자동 저장됨');
  }, [post, toast]);

  const handleTableMouseDown = useCallback((e, table, mode) => {
    if (e.button !== 0) return;

    dragRef.current = {
      active: true,
      mode,
      id: Number(table.id),
      startX: e.clientX,
      startY: e.clientY,
      origLeft: Number(table.x || 0),
      origTop: Number(table.y || 0),
      origW: Number(table.w || 120),
      origH: Number(table.h || 80),
    };

    setSelected(table.id);
    e.preventDefault();
    e.stopPropagation();
  }, [setSelected]);

  useEffect(() => {
    const onMove = (ev) => {
      if (dragRef.current.active) {
        const d = dragRef.current;
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        const floorSize = getFloorSize();

        if (d.mode === 'drag') {
          let newL = snap(d.origLeft + dx);
          let newT = snap(d.origTop + dy);

          const current = boot.tables.find((t) => Number(t.id) === Number(d.id));
          const currentW = Number(current?.w || d.origW);
          const currentH = Number(current?.h || d.origH);

          newL = clamp(newL, 0, Math.max(0, floorSize.w - currentW));
          newT = clamp(newT, 0, Math.max(0, floorSize.h - currentH));

          updateTableLocal(d.id, { x: newL, y: newT });
        } else {
          let newW = snap(d.origW + dx);
          let newH = snap(d.origH + dy);

          newW = Math.max(MIN_W, newW);
          newH = Math.max(MIN_H, newH);

          if (d.origLeft + newW > floorSize.w) {
            newW = floorSize.w - d.origLeft;
            newW = Math.max(MIN_W, snap(newW));
          }
          if (d.origTop + newH > floorSize.h) {
            newH = floorSize.h - d.origTop;
            newH = Math.max(MIN_H, snap(newH));
          }

          updateTableLocal(d.id, { w: newW, h: newH });
        }
        return;
      }

      if (drawRef.current.active) {
        const pos = getFloorXYFromClient(ev.clientX, ev.clientY);
        const floorSize = getFloorSize();

        let currX = clamp(snap(pos.x), 0, floorSize.w);
        let currY = clamp(snap(pos.y), 0, floorSize.h);

        const left = Math.min(drawRef.current.startX, currX);
        const top = Math.min(drawRef.current.startY, currY);
        const width = Math.max(0, Math.abs(currX - drawRef.current.startX));
        const height = Math.max(0, Math.abs(currY - drawRef.current.startY));

        drawRef.current = {
          ...drawRef.current,
          left,
          top,
          width,
          height,
        };
        setBoot((prev) => ({ ...prev }));
      }
    };

    const onUp = async () => {
      if (dragRef.current.active) {
        const d = dragRef.current;
        dragRef.current.active = false;

        const current = boot.tables.find((t) => Number(t.id) === Number(d.id));
        if (current) {
          await saveTablePosition(current);
        }
      }

      if (drawRef.current.active) {
        const d = { ...drawRef.current };
        drawRef.current.active = false;

        if (d.width < MIN_W || d.height < MIN_H) {
          setBoot((prev) => ({ ...prev }));
          return;
        }

        const r = await post('add_table', {
          name: newName.trim() || '테이블',
          shape: newShape || 'rect',
          x: d.left,
          y: d.top,
          w: d.width,
          h: d.height,
        });

        if (!r.success) {
          toast(r.message || '추가 실패', false);
          setBoot((prev) => ({ ...prev }));
          return;
        }

        const t = r.table;
        setBoot((prev) => ({
          ...prev,
          tables: [...prev.tables, t],
        }));
        setSelected(t.id);
        toast('드래그 생성됨');
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [
    boot.tables,
    clamp,
    getFloorSize,
    getFloorXYFromClient,
    newName,
    newShape,
    post,
    saveTablePosition,
    setSelected,
    snap,
    toast,
    updateTableLocal,
  ]);

  const handleFloorMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (e.target !== floorRef.current) return;

    setSelected(null);

    const pos = getFloorXYFromClient(e.clientX, e.clientY);
    drawRef.current = {
      active: true,
      startX: clamp(snap(pos.x), 0, floorRef.current.clientWidth),
      startY: clamp(snap(pos.y), 0, floorRef.current.clientHeight),
      left: clamp(snap(pos.x), 0, floorRef.current.clientWidth),
      top: clamp(snap(pos.y), 0, floorRef.current.clientHeight),
      width: 0,
      height: 0,
    };

    e.preventDefault();
  }, [clamp, getFloorXYFromClient, setSelected, snap]);

  if (boot.loading) {
    return <div className="container-fluid py-3">불러오는 중...</div>;
  }

  if (boot.error) {
    return <div className="container-fluid py-3 text-danger">{boot.error}</div>;
  }

  return (
    <>
      <style>{`
        :root{
          --grid-size: 20px;
          --grid-line: #e5e7eb;
          --floor-bg: #ffffff;
        }
        .editor-wrap{max-width:1600px;margin:0 auto;}
        .floor-shell{
          border:1px solid #e5e7eb;
          border-radius:12px;
          overflow:hidden;
          background:#f8f9fa;
        }
        .floor-viewport{
          overflow:auto;
          height: calc(100vh - 220px);
          min-height: 500px;
          background:#eef1f4;
          display:flex;
          justify-content:center;
          align-items:center;
          padding:24px;
        }
        .floor{
          position:relative;
          box-shadow:0 8px 30px rgba(0,0,0,.08);
          flex:0 0 auto;
        }
        .tbox{
          position:absolute;
          border:1px solid rgba(0,0,0,.18);
          border-radius:12px;
          box-shadow:0 2px 10px rgba(0,0,0,.07);
          background:#ffffffd8;
          backdrop-filter: blur(2px);
          display:flex;
          align-items:center;
          justify-content:center;
          text-align:center;
          cursor:grab;
          user-select:none;
          padding:6px;
          overflow:hidden;
        }
        .tbox.circle{border-radius:999px;}
        .tbox.inactive{opacity:.35; filter:grayscale(1);}
        .tbox.selected{outline:2px solid #0d6efd; outline-offset:1px;}
        .tname{
          font-weight:800;
          font-size:14px;
          line-height:1.15;
          white-space:normal;
          word-break:keep-all;
          pointer-events:none;
        }
        .resize-handle{
          position:absolute;
          width:14px;
          height:14px;
          right:-7px;
          bottom:-7px;
          background:#0d6efd;
          border-radius:6px;
          border:2px solid #fff;
          cursor:nwse-resize;
        }
        .draw-preview{
          position:absolute;
          border:2px dashed #0d6efd;
          background:rgba(13,110,253,.08);
          pointer-events:none;
          z-index:50;
        }
        .hint{font-size:12px;color:#6c757d;}
        .status-dot{
          width:8px;height:8px;border-radius:999px;background:#198754;display:inline-block;margin-right:6px;
        }
        .toast-wrap{position:fixed;right:16px;bottom:16px;z-index:9999;}
        .mini-badge{
          display:inline-flex;
          align-items:center;
          gap:6px;
          font-size:12px;
          padding:4px 8px;
          border-radius:999px;
          background:#f1f3f5;
          color:#495057;
          border:1px solid #dee2e6;
          margin-right:6px;
          margin-bottom:6px;
        }
      `}</style>

      <div className="editor-wrap container-fluid py-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <div>
            <h5 className="m-0">도면/테이블 배치 편집기</h5>
            <div className="hint mt-1">
              빈 공간 드래그로 테이블 생성 / 드래그 이동 / 우하단 파란 점 리사이즈 / 변경 시 자동 저장
            </div>
          </div>
          <div className="d-flex gap-2">
            <a href="/pos" className="btn btn-outline-dark btn-sm">POS 화면 보기</a>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-12 col-lg-4">
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="fw-bold mb-2">도면 설정</div>

                <div className="row g-2">
                  <div className="col-6">
                    <label className="form-label small">캔버스 가로(px)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={canvasW}
                      min="320"
                      max="4000"
                      onChange={(e) => setCanvasW(e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">캔버스 세로(px)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={canvasH}
                      min="320"
                      max="4000"
                      onChange={(e) => setCanvasH(e.target.value)}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label small">배경 이미지 경로(선택)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={bgPath}
                      placeholder="예: /img/floorplans/shop1.png"
                      onChange={(e) => setBgPath(e.target.value)}
                    />
                    <div className="hint mt-1">배경 이미지는 grid 위가 아니라 아래에 깔림.</div>
                  </div>
                </div>

                <div className="d-flex gap-2 mt-3">
                  <button className="btn btn-primary" onClick={handleSaveFloor}>도면 저장</button>
                  <button className="btn btn-outline-secondary" onClick={handleApplyFloor}>화면에 적용</button>
                </div>

                <hr />

                <div className="fw-bold mb-2">테이블 추가</div>
                <div className="row g-2">
                  <div className="col-8">
                    <input
                      type="text"
                      className="form-control"
                      value={newName}
                      placeholder="예: 1번테이블"
                      onChange={(e) => setNewName(e.target.value)}
                    />
                  </div>
                  <div className="col-4">
                    <select
                      className="form-select"
                      value={newShape}
                      onChange={(e) => setNewShape(e.target.value)}
                    >
                      <option value="rect">사각</option>
                      <option value="circle">원형</option>
                    </select>
                  </div>
                  <div className="col-12 d-grid">
                    <button className="btn btn-success" onClick={handleAddTable}>기본 크기로 추가</button>
                  </div>
                </div>

                <div className="mt-3">
                  <span className="mini-badge">grid 20px snap</span>
                  <span className="mini-badge">빈 공간 드래그 생성</span>
                  <span className="mini-badge">가운데 정렬</span>
                </div>

                <hr />

                <div className="fw-bold mb-2">선택된 테이블</div>
                <div className="border rounded p-2">
                  {!selectedTable ? (
                    <div className="text-muted">테이블을 클릭하세요.</div>
                  ) : (
                    <>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className="fw-bold">#{selectedId}</div>
                        <button className="btn btn-outline-danger btn-sm" onClick={handleDelete}>
                          삭제
                        </button>
                      </div>

                      <label className="form-label small">이름</label>
                      <input
                        className="form-control mb-2"
                        value={selectedName}
                        onChange={(e) => setSelectedName(e.target.value)}
                      />

                      <div className="row g-2">
                        <div className="col-6">
                          <label className="form-label small">모양</label>
                          <select
                            className="form-select"
                            value={selectedShape}
                            onChange={(e) => setSelectedShape(e.target.value)}
                          >
                            <option value="rect">사각</option>
                            <option value="circle">원형</option>
                          </select>
                        </div>
                        <div className="col-6">
                          <label className="form-label small">활성</label>
                          <select
                            className="form-select"
                            value={selectedActive}
                            onChange={(e) => setSelectedActive(e.target.value)}
                          >
                            <option value="1">사용</option>
                            <option value="0">숨김</option>
                          </select>
                        </div>
                      </div>

                      <div className="d-grid mt-2">
                        <button className="btn btn-primary" onClick={handleMetaSave}>저장</button>
                      </div>

                      <div className="hint mt-2">
                        위치/크기 변경은 자동 저장. 이름/모양/활성은 여기서 저장.
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-8">
            <div className="floor-shell shadow-sm">
              <div className="px-3 py-2 border-bottom bg-white d-flex align-items-center justify-content-between">
                <div className="small"><span className="status-dot"></span> 편집 모드</div>
                <div className="small text-muted">캔버스: {boot.canvas_w}×{boot.canvas_h}</div>
              </div>

              <div className="floor-viewport">
                <div
                  ref={floorRef}
                  className="floor"
                  style={floorStyle}
                  onMouseDown={handleFloorMouseDown}
                >
                  {boot.tables.map((t) => (
                    <div
                      key={t.id}
                      className={[
                        'tbox',
                        t.shape === 'circle' ? 'circle' : '',
                        Number(t.active) === 1 ? '' : 'inactive',
                        Number(selectedId) === Number(t.id) ? 'selected' : '',
                      ].join(' ')}
                      style={{
                        left: `${parseInt(t.x, 10)}px`,
                        top: `${parseInt(t.y, 10)}px`,
                        width: `${parseInt(t.w, 10)}px`,
                        height: `${parseInt(t.h, 10)}px`,
                      }}
                      onMouseDown={(e) => handleTableMouseDown(e, t, 'drag')}
                    >
                      <div className="tname">{t.name}</div>
                      <div
                        className="resize-handle"
                        title="리사이즈"
                        onMouseDown={(e) => handleTableMouseDown(e, t, 'resize')}
                      ></div>
                    </div>
                  ))}

                  {drawRef.current.active && (
                    <div
                      className="draw-preview"
                      style={{
                        left: `${drawRef.current.left}px`,
                        top: `${drawRef.current.top}px`,
                        width: `${drawRef.current.width}px`,
                        height: `${drawRef.current.height}px`,
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="toast-wrap">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`alert ${t.ok ? 'alert-success' : 'alert-danger'} py-2 px-3 shadow-sm`}
              style={{ marginBottom: '8px' }}
            >
              {t.msg}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default PosFloorEditor;